import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useConversations } from '@/context/ConversationsContext';
import { api } from '@/lib/apiClient';

/** Request browser notification permission (call from a user gesture). */
export function requestNotificationPermission(): void {
  if ('Notification' in window && Notification.permission === 'default') {
    Notification.requestPermission().catch(() => {});
  }
}

/** Draw an unread-count badge onto the favicon. */
function updateFavicon(count: number): void {
  const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) return;

  if (count === 0) {
    link.href = '/favicon.png';
    return;
  }

  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 32;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(img, 0, 0, 32, 32);
    ctx.beginPath();
    ctx.arc(23, 9, 9, 0, 2 * Math.PI);
    ctx.fillStyle = '#DC2626';
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 12px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(count > 9 ? '9+' : String(count), 23, 10);
    link.href = canvas.toDataURL('image/png');
  };
  img.src = '/favicon.png';
}

/**
 * App-wide unread badge (tab title + favicon) and browser notifications for
 * new messages while the tab is hidden. Cross-tab duplicate suppression uses
 * a localStorage try-lock keyed by conversation + message timestamp.
 */
export default function NotificationsManager() {
  const { conversations, totalUnread } = useConversations();
  const { user, organizations } = useAuth();
  const navigate = useNavigate();
  const lastSeenRef = useRef<Map<string, string>>(new Map());
  const primedRef = useRef(false);
  const mutedKeysRef = useRef<Set<string>>(new Set());

  // Title + favicon badge
  useEffect(() => {
    document.title = totalUnread > 0 ? `(${totalUnread > 99 ? '99+' : totalUnread}) Flock` : 'Flock';
    updateFavicon(totalUnread);
  }, [totalUnread]);

  // Load mute settings for every community group (small N) to suppress
  // notifications for muted conversations.
  const groupIdsKey = useMemo(
    () =>
      organizations
        .flatMap((o) => o.groups.map((g) => g.id))
        .sort()
        .join(','),
    [organizations]
  );
  useEffect(() => {
    const groupIds = groupIdsKey ? groupIdsKey.split(',') : [];
    let cancelled = false;
    (async () => {
      const muted = new Set<string>();
      await Promise.all(
        groupIds.map(async (gid) => {
          try {
            const response = await api.get<{
              data: { mutedChats?: Record<string, boolean> };
            }>(`/groups/${gid}/chat/settings`);
            for (const [key, value] of Object.entries(response.data?.mutedChats ?? {})) {
              if (value) muted.add(key === gid ? `everyone_${gid}` : key);
            }
          } catch {
            // Non-blocking
          }
        })
      );
      if (!cancelled) mutedKeysRef.current = muted;
    })();
    return () => {
      cancelled = true;
    };
  }, [groupIdsKey]);

  // Browser notifications on new messages while hidden
  useEffect(() => {
    const seen = lastSeenRef.current;

    // First pass: prime the baseline without notifying (page load)
    if (!primedRef.current) {
      if (conversations.length > 0) {
        for (const conv of conversations) {
          if (conv.lastMessageAt) seen.set(conv.key, conv.lastMessageAt);
        }
        primedRef.current = true;
      }
      return;
    }

    for (const conv of conversations) {
      if (!conv.lastMessageAt) continue;
      const prev = seen.get(conv.key);
      if (prev === conv.lastMessageAt) continue;
      seen.set(conv.key, conv.lastMessageAt);
      if (!prev) continue; // newly appeared conversation — don't notify retroactively

      const isOwn = conv.lastMessageUserName && conv.lastMessageUserName === user?.name;
      const isMuted = mutedKeysRef.current.has(conv.key);
      const tabVisible = document.visibilityState === 'visible';
      if (isOwn || isMuted || tabVisible) continue;
      if (!('Notification' in window) || Notification.permission !== 'granted') continue;

      // Cross-tab dedupe: first tab to claim the lock notifies
      const lockKey = `flock_notify_${conv.key}_${conv.lastMessageAt}`;
      try {
        if (localStorage.getItem(lockKey)) continue;
        localStorage.setItem(lockKey, '1');
        setTimeout(() => localStorage.removeItem(lockKey), 30_000);
      } catch {
        // Storage unavailable — notify anyway
      }

      const title = conv.type === 'everyone' ? `${conv.groupName} · Everyone` : conv.name;
      const body = conv.lastMessageUserName
        ? `${conv.lastMessageUserName}: ${conv.lastMessageText ?? 'New message'}`
        : (conv.lastMessageText ?? 'New message');
      try {
        const notification = new Notification(title, {
          body: body.slice(0, 140),
          icon: '/favicon.png',
          tag: conv.key, // collapse repeated notifications per conversation
        });
        notification.onclick = () => {
          window.focus();
          navigate(
            conv.chatGroupId ? `/chat/${conv.groupId}/${conv.chatGroupId}` : `/chat/${conv.groupId}`
          );
          notification.close();
        };
      } catch {
        // Some browsers restrict constructor — ignore
      }
    }
  }, [conversations, user?.name, navigate]);

  return null;
}
